import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Dropdown from "@mui/joy/Dropdown";
import IconButton from "@mui/joy/IconButton";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Typography from "@mui/joy/Typography";
import React from "react";
import { User, getUserPermissions } from "../../../models/auth";
import UnifiedDataTable, { DataTableColumn } from "../../../shared/components/ui/UnifiedDataTable";

interface UsersTableProps {
  readonly users: User[];
  readonly editUserAttributes: (user: User) => void;
  readonly editUserPwd: (user: User) => void;
  readonly deleteUser: (email: string) => void;
}

export default function UsersTable({
  users,
  editUserAttributes,
  editUserPwd,
  deleteUser,
}: UsersTableProps) {
  const columns: DataTableColumn<User>[] = [
    {
      key: "email",
      header: "Username",
      width: "35ch",
      render: (user) => <Typography level="body-sm">{user.email}</Typography>,
    },
    {
      key: "given_name",
      header: "Given name",
      render: (user) => <Typography level="body-sm">{user.attributes.given_name}</Typography>,
    },
    {
      key: "name",
      header: "Last name",
      render: (user) => <Typography level="body-sm">{user.attributes.name}</Typography>,
    },
    {
      key: "permissions",
      header: "Permissions",
      render: (user) => (
        <Typography level="body-xs">{getUserPermissions(user.attributes).join(", ")}</Typography>
      ),
    },
    {
      key: "organization",
      header: "Organization",
      render: (user) => <Typography level="body-sm">{user.attributes.organization}</Typography>,
    },
    {
      key: "actions",
      header: "",
      width: "80px",
      render: (user) => (
        <Dropdown>
          <MenuButton
            slots={{ root: IconButton }}
            slotProps={{ root: { variant: "plain", color: "neutral", size: "sm" } }}
          >
            <MoreVertIcon />
          </MenuButton>
          <Menu placement="bottom-end">
            <MenuItem onClick={() => editUserAttributes(user)}>
              <ListItemDecorator>
                <EditIcon />
              </ListItemDecorator>
              Edit user
            </MenuItem>
            <MenuItem onClick={() => editUserPwd(user)}>
              <ListItemDecorator>
                <LockResetIcon />
              </ListItemDecorator>
              Reset password
            </MenuItem>
            <MenuItem onClick={() => deleteUser(user.email)} color="danger">
              <ListItemDecorator>
                <DeleteIcon />
              </ListItemDecorator>
              Delete user
            </MenuItem>
          </Menu>
        </Dropdown>
      ),
    },
  ];

  return (
    <UnifiedDataTable
      data={users}
      columns={columns}
      getItemId={(user) => user.email}
      ariaLabel="users table"
      height="auto"
      showPagination={false}
      emptyState={{
        title: "No users found",
        description: "There are no users in the system yet.",
      }}
    />
  );
}
